import type { CatalogPricingRuleModel } from '@/shared/generated'
import type { AxiosError } from 'axios'
import type { BooleanExpr, RuleParam } from '../model/types'

import { BOOLEAN_OP_ARITY, COMPETITOR_AGGREGATIONS } from './constants'

type NormalizedTacticParams = {
	pricing_rule_id: string | null
	pricing_rule_version_id: string | null
	rule_params: Record<string, unknown>
}

/**
 * RB tactic params in the current shape. Campaigns saved before the rule-builder
 * migration — and system rules, which have no versions — store `rule_id` instead.
 */
export const normalizeTacticParams = (params: any): NormalizedTacticParams => ({
	pricing_rule_id: params?.pricing_rule_id ?? params?.rule_id ?? null,
	pricing_rule_version_id: params?.pricing_rule_version_id ?? null,
	rule_params: params?.rule_params ?? {},
})

/** Catalog row for a tactic — matched by version when there is one, else by rule id. */
export const findCatalogVariant = (
	catalog: CatalogPricingRuleModel[],
	{ pricing_rule_id, pricing_rule_version_id }: NormalizedTacticParams
): CatalogPricingRuleModel | undefined =>
	catalog.find((v) =>
		pricing_rule_version_id
			? v.version_id === pricing_rule_version_id
			: v.id === pricing_rule_id
	)

/**
 * Competitor names a rule selects, across every competitor_list param merged
 * into one list. Values are names, not ids — that is what the fields store.
 */
export const competitorsFromTactic = (
	variant: CatalogPricingRuleModel | undefined,
	ruleParams: Record<string, unknown> | undefined
): string[] =>
	((variant?.params ?? []) as RuleParam[])
		.filter((p) => p.type === 'competitor_list')
		.flatMap((p) => {
			const value = ruleParams?.[p.id]

			return Array.isArray(value) ? value : value ? [value] : []
		})
		.filter(Boolean) as string[]

/**
 * Allowed aggregations for a param: rule-declared when present, else the built-in
 * competitor catalog — a param only ever reaches an aggregation through a
 * competitor node. Shared by the validator and the field so the two can never
 * disagree — a rule declaring `average` was rejected by one and unofferable by
 * the other when they each hardcoded the catalog.
 */
export const aggregationOptionsFor = (
	param: Pick<RuleParam, 'options'>
): readonly string[] =>
	param.options?.length ? param.options : COMPETITOR_AGGREGATIONS

/** Expected argument count for a boolean operation; `'n'` means variadic. */
export const getBooleanOpArity = (op: BooleanExpr['operation']): number | 'n' =>
	BOOLEAN_OP_ARITY[op]

/** Split a `ref` string into its namespace and the remaining path. */
export const parseRef = (ref: string): { namespace: string; path: string } => {
	const dot = ref.indexOf('.')
	if (dot === -1) return { namespace: ref, path: '' }

	return { namespace: ref.slice(0, dot), path: ref.slice(dot + 1) }
}

/** Returns true when the error is an HTTP 409 Conflict response. */
export const isConflictError = (e: unknown): boolean =>
	(e as AxiosError)?.response?.status === 409
